/** @format */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { LocalText } from "../../resources/BLResourceTypes";
import { useLocale } from "../../../i18n/hooks";
import { useDeleteUserMutation } from "../../../app/services/userApi";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";
import { useAppDispatch } from "../../../app/hooks";
import { setUserToken } from "../../auth/authSlice";
import { apiSlice } from "../../../app/apiSlice";
import HelpLink from "../../helpLink/HelpLink";

const DeleteUser: React.FC = () => {
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const dispatch = useAppDispatch();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>("");

  const [deleteUser] = useDeleteUserMutation();

  const instructions: LocalText[] = [
    {
      en: "Are you sure you want to delete your account?",
      cy: "Ydych chi'n sicr eich bod eisiau dileu eich cyfrif?",
    },
    {
      en: "This action cannot be undone.",
      cy: "Ni ellir dadwneud y cam hwn.",
    },
    {
      en: "Deleting your account will delete ALL resources you've created.",
      cy: "Bydd dileu eich cyfrif yn dileu POB adnodd rydych chi wedi'i greu.",
    },
    {
      en: "To confirm you wish to delete your account, type 'DELETE' into the following box:",
      cy: "I gadarnhau eich bod eisiau dileu eich cyfrif, teipiwch 'DILEU' yn y blwch canlynol:",
    },
  ];

  const cleanup = () => {
    setConfirmationText("");
    setShowModal(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      event.stopPropagation();

      if (confirmationText === (locale === "en" ? "DELETE" : "DILEU")) {
        // DELETE ACCOUNT
        deleteUser();

        // CLEAR SESSION
        dispatch(setUserToken(undefined));
        dispatch(
          apiSlice.util.invalidateTags([
            "User",
            "UserResourceRef",
            "Resources",
            "ResourceRef",
          ]),
        );
        localStorage.removeItem("BL_CMS_STATE");

        cleanup();

        // REDIRECT
        window.location.href = "https://resources.wjec.co.uk/"; // TODO: CHECK LOCALE FOR CBAC
      }
    } catch {
      console.log("Failed to delete user");
    }
  };

  return (
    <>
      <Button variant="danger" onClick={() => setShowModal(true)}>
        {_("Delete Account")}
      </Button>
      <Modal
        show={showModal}
        onHide={() => cleanup()}
        aria-label="delete-user-resource-modal"
        centered
      >
        <Modal.Header closeButton>
          <span className="flex-grow-1">{_("Delete Account")}?</span>
          <HelpLink position="inherit" className="p-0 help-link" />
        </Modal.Header>
        <Modal.Body>
          <Form role="form" onSubmit={handleSubmit}>
            <p>
              {instructions[0][locale]}
              <br />
              <strong>{instructions[1][locale]}</strong>
            </p>
            <p className="mb-1">{instructions[3][locale]}</p>
            <Form.Control
              name="delete-confirmation"
              autoComplete="off"
              aria-describedby="Delete Confirmation box"
              isInvalid={
                confirmationText !== (locale === "en" ? "DELETE" : "DILEU")
              }
              placeholder={_("Delete").toUpperCase()}
              onChange={(e) => setConfirmationText(e.target.value)}
              value={confirmationText}
            />
            <Alert variant="danger" className="d-flex flex-row gap-2 mt-3">
              <ExclamationTriangle className="mt-1" />
              {instructions[2][locale]}
            </Alert>
            <div className="d-flex gap-3 justify-content-end">
              <Button
                role="button"
                variant="outline-editing"
                onClick={() => cleanup()}
              >
                {_("Cancel")}
              </Button>
              <Button role="button" type="submit" variant="danger">
                {_("Delete")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DeleteUser;
